import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { selectVirtualsVerdict } from 'src/app/store/user-details/user-details.selectors';
import { IAccountVirtualsVerdict } from 'src/app/store/user-details/user-details.state';
import { isVirtualsSectionOn, providersEnabled } from '../guards/providers-enabled.guard';
import { SettingsService } from './settings.service';
import { isVerdictVisibleFor } from './virtuals-verdict';

// Whether the current visitor may see the VIRTUEL entries (header and burger
// menu) pointing at /virtuals. Decided from data the app already holds — NO
// network call of its own:
//
//   providersEnabled()                 the build-time kill switch (config)
//   && virtualsEnabled$ === true       the runtime switch (/settings)
//   && credential.uid                  someone is signed in
//   && details.fbId === credential.uid the verdict is THIS account's
//   && details.virtuals.visible        the server said an explicit true
//
// The verdict is the SERVER's (GET /user/getUserAccount `virtuals`, design
// D1/D2): role, account status, provider state, currency allowlist, rollout
// rule and block, all in one place, and the same one ProvidersEnabledGuard
// enforces on the route. The two switches stay as defence in depth (design
// D6): a runtime or build-time "off" closes the entry whatever an account
// payload says.
//
// WHY no network call any more (design D14). This used to switchMap to the
// gateway's GET /catalog for the currency allowlist. That function cold-starts,
// the bounded retry gave up after a second, and the header's subscription
// never re-dialled — so a cold start hid the entry until a reload, and a slow
// one made it "appear after 5 s". The account payload is fetched at boot and
// login anyway, so the entry now appears the moment that answer lands.
//
// The fbId check closes the account-switch window: logout used to leave the
// previous account in the store, and a no-reload login computes against the
// old details until the new payload lands. A verdict is never applied to a
// credential it was not computed for.
//
// SettingsService.virtualsEnabled$ has no value until the settings call
// answers (and answers false when it fails), so combineLatest stays silent —
// entries hidden — until the backend has been asked. On the server (SSR)
// there is no credential, so the answer is false there and nothing is
// fetched; a per-user verdict must never reach an SSR render.
//
// Shared: the header and the burger menu subscribe for the life of the app;
// shareReplay gives them ONE upstream subscription and one answer, and
// refCount lets it go when both are gone.
@Injectable({ providedIn: 'root' })
export class ProvidersVisibilityService {
  readonly canSeeVirtuals$: Observable<boolean>;

  constructor(authService: AuthService, store: Store, settingsService: SettingsService) {
    this.canSeeVirtuals$ = combineLatest([
      authService.authStatusSub,
      store.select(selectVirtualsVerdict),
      settingsService.virtualsEnabled$,
    ]).pipe(
      map(([credential, verdict, runtimeOn]: [any, IAccountVirtualsVerdict, boolean]) =>
        isVirtualsSectionOn(providersEnabled(), runtimeOn) &&
        !!credential &&
        !!credential.uid &&
        isVerdictVisibleFor(verdict, credential.uid)
      ),
      // Upstream re-emits on every credential token refresh and every new
      // account payload; the entry only cares when the answer moves.
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}
